import type { ChatMessage } from './chat-types';
import type { SeedThread } from './thread-seed';
import { PB_RECOVERY_SECTION } from './rule-set-retrieval';

/** Design-basis stand-in date — threads sort with other pinned alpha history. */
const UPDATED_AT = Date.UTC(2026, 5, 8);

const MA_PB_MESSAGES: ChatMessage[] = [
  {
    id: 1,
    who: 'user',
    time: '11:35',
    body: 'How does the Pb reconciliation check close now?',
  },
  {
    id: 2,
    who: 'bot',
    time: '11:36',
    body:
      'Both paths agree: 1,800 stpd × 2.40% Pb × 88.2% recovery gives 38.10 dst Pb/day, and 56.87 dst/day of concentrate at 67.0% Pb gives the same 38.10 dst Pb/day. Closure 0.3% — consistent, the tolerance Zn closes at.',
    parts: [
      {
        type: 'tool',
        verb: 'read',
        preview: 'Check-in / check-out: cross-checking the two source documents',
      },
    ],
  },
  {
    id: 3,
    who: 'user',
    time: '11:42',
    body: 'How was this found — manual review or something automatic?',
  },
  {
    id: 4,
    who: 'bot',
    time: '11:45',
    body:
      'Automatically, by reconciling the two source documents against each other: fresh feed 1,800 stpd at 2.40% Pb gives 43.20 dst Pb/day in; final concentrate 56.87 dst/day at 67.0% Pb gives 38.10 dst Pb/day on a mass-basis, matching the recovery path. The sole fresh-feed anchor is WIT-021 with no independent cross-check — a calibration drift would silently bias every downstream number.',
    citations: [{ label: 'A §M2 §6.2' }, { label: 'B: slide 07' }],
  },
];

const PM_BOTTLENECK_MESSAGES: ChatMessage[] = [
  {
    id: 1,
    who: 'user',
    time: '09:00',
    body: 'Where is the plant constraint right now on the design basis?',
  },
  {
    id: 2,
    who: 'bot',
    time: '09:00',
    body:
      'Design-basis: primary grinding (mill + cyclone) is the tightest subprocess — 75 stph at 83% of the 90 stph normal band; 4 of 5 cyclones in service; 248% circulating load.',
    parts: [
      {
        type: 'tool',
        verb: 'read',
        preview: 'Primary grinding (mill + cyclone)',
      },
    ],
  },
  {
    id: 3,
    who: 'user',
    time: '09:01',
    body: 'How much headroom before it binds?',
  },
  {
    id: 4,
    who: 'bot',
    time: '09:02',
    body:
      'Runner-up is cyclone cluster spare capacity at 80% — 3 points behind. Crushing sits at 50%, mill motor power 67%, Pb/Ag flotation 44%, Zn flotation 48% — not the constraint.',
    citations: [{ label: 'B: slide 05' }],
  },
];

const ADV_ZN_CASE_MESSAGES: ChatMessage[] = [
  {
    id: 1,
    who: 'user',
    time: '07:10',
    body: 'Walk me through CASE-001 ZN-REC.',
  },
  {
    id: 2,
    who: 'bot',
    time: '07:10',
    body:
      'ZN-REC opened 06:50 at 91% confidence. Recovery (24h avg) 82.3% vs design 85.1%. Zn circuit pH in-spec only 68% of this shift. CuSO4 activation at 155 g/t (design 150) is adequate. Recovery has not improved over the last 8 days. Cause candidate: pH instability is the primary driver. SMBS carry-over from the Pb circuit may be suppressing CuSO4 activation in the Zn conditioner.',
    parts: [
      {
        type: 'tool',
        verb: 'read',
        preview: 'Zinc recovery 3.3% below design',
      },
    ],
  },
  {
    id: 3,
    who: 'user',
    time: '07:12',
    body: 'What evidence is still open, and what is the approved action?',
  },
  {
    id: 4,
    who: 'bot',
    time: '07:13',
    body:
      'Two open evidence requests: operator visual — SMBS dosing check in the Pb circuit; CRO — confirm lime loop AIC5002 control mode. One confidence discount: the 10:00 Zn Rougher Conc sample (SA04) was missed, so the grade call is discounted. Respond by 12:30 or it escalates to the superintendent. Approved action: increase lime to Zn circuit — target pH 11.0–11.2.',
    citations: [{ label: 'B: slide 05' }],
  },
];

const ADV_PH_ALARM_MESSAGES: ChatMessage[] = [
  {
    id: 1,
    who: 'user',
    time: '06:55',
    body: 'What happened with the 06:42 ZN-PH alarm?',
  },
  {
    id: 2,
    who: 'bot',
    time: '06:56',
    body:
      'ZN-PH-01, P2 — Zn Float pH LOW 10.2 (SP=10.8); below the 10.8–11.2 band at 06:42. The immediate-response rule routed zero-touch: IF Zn float pH < SP low limit THEN increase lime SP to the Zn circuit. Control-mode check: AIC5002 in AUTO. D. Okafor picked it up 06:43; resolved 06:55.',
    parts: [
      {
        type: 'tool',
        verb: 'read',
        preview: 'ZN-PH-01 — immediate response: increase lime SP',
      },
    ],
    citations: [{ label: 'FMEA-IRA' }],
  },
  {
    id: 3,
    who: 'user',
    time: '07:00',
    body: 'Does that connect to the Zn recovery case?',
  },
  {
    id: 4,
    who: 'bot',
    time: '07:00',
    body:
      'Yes — the same lever. ZN-REC\'s approved action is increase lime to Zn circuit — target pH 11.0–11.2 (pH instability is its primary cause candidate).',
    citations: [{ label: 'FMEA-IRA' }, { label: 'B: slide 05' }],
  },
];

const ADV_PB_RECOVERY_MESSAGES: ChatMessage[] = [
  {
    id: 1,
    who: 'user',
    time: '08:20',
    body: 'Pb recovery is drifting below target — what does the rule set actually cover for this?',
  },
  {
    id: 2,
    who: 'bot',
    time: '08:20',
    body: 'Direct retrieval from the live rule set — one line per registered cause, no free-text diagnosis.',
    parts: [
      {
        type: 'tool',
        verb: 'read',
        preview: 'Rule set · Pb Recovery Below Target',
      },
      {
        // Computed at render time from the session's live advisory state —
        // never a frozen string — so this thread tracks the Rule set panel
        // through an in-session approval (review MUST-FIX M4). See
        // rule-set-retrieval.ts § pbRecoveryRetrieval.
        type: 'rule-set-retrieval',
        section: PB_RECOVERY_SECTION,
      },
    ],
  },
];

export const OPTIFLOW_SEED_THREADS: SeedThread[] = [
  {
    id: 'thread-ma-pb-reconciliation',
    title: 'Pb reconciliation — closed paths',
    pinned: true,
    updatedAt: UPDATED_AT,
    projectSlug: 'alpha',
    messages: MA_PB_MESSAGES,
  },
  {
    id: 'thread-pm-bottleneck',
    title: 'Bottleneck and headroom read',
    pinned: true,
    updatedAt: UPDATED_AT,
    projectSlug: 'alpha',
    messages: PM_BOTTLENECK_MESSAGES,
  },
  {
    id: 'thread-adv-zn-case',
    title: 'CASE-001 ZN-REC — walkthrough',
    pinned: true,
    updatedAt: UPDATED_AT,
    projectSlug: 'alpha',
    messages: ADV_ZN_CASE_MESSAGES,
  },
  {
    id: 'thread-adv-ph-alarm',
    title: 'ZN-PH-01 — zero-touch response',
    pinned: true,
    updatedAt: UPDATED_AT,
    projectSlug: 'alpha',
    messages: ADV_PH_ALARM_MESSAGES,
  },
  {
    id: 'thread-adv-pb-recovery',
    title: 'Pb recovery drifting — rule set retrieval',
    pinned: true,
    updatedAt: UPDATED_AT,
    projectSlug: 'alpha',
    messages: ADV_PB_RECOVERY_MESSAGES,
  },
];
